import { TableProps, TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { Check, Copy, Edit, PlusCircle, Trash2 } from 'feather-icons-react';
import { useState, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { copyToClipboard } from 'utils';

import { ReactComponent as IconChevronSort } from 'assets/images/icons/chevron-selector-vertical.svg';

import { Button, IconButton, Input, Spin, Table } from 'components/common';

import NotificationService from 'modules/notifications/notificationService';
import { useOrganization } from 'modules/organization/useOrganization';
import { PIXEL_CDN_URL } from 'modules/pixel/constants';
import { pixelApi } from 'modules/pixel/pixelApi';
import { ConversionRule, FrequencyType, PixelConfigDTO } from 'modules/pixel/pixelApiTypes';
import { usePixelConfig } from 'modules/pixel/usePixelConfig';

import { ConversionRuleModal } from './ConversionRuleModal';
import { DeleteRuleModal } from './DeleteRuleModal';

const sortIcon = () => <IconChevronSort width={20} height={20} />;

const sortDirections: Record<string, string> = {
  ascend: 'ASC',
  descend: 'DESC',
};

// Format frequency for display
const formatFrequency = (frequency: FrequencyType | null | undefined): string => {
  if (!frequency) return '-';
  switch (frequency) {
    case 'perPage':
      return 'Per page';
    case 'perSession':
      return 'Per session';
    case 'onceOnLoad':
      return 'Once on load';
    default:
      return frequency;
  }
};

export const PixelCode = () => {
  const { currentAdvertiser } = useOrganization();
  const advertiserId = currentAdvertiser?.id;
  const pixelCode = currentAdvertiser?.pixelCode;

  const { config, isLoading, error, saveConfig, fetchConfig } = usePixelConfig({
    advertiserId: advertiserId || null,
    autoFetch: true,
  });

  // Local state
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [showDeleteRuleModal, setShowDeleteRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ConversionRule | null>(null);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | undefined>(undefined);
  const [deletingRule, setDeletingRule] = useState<{ rule: ConversionRule; index: number } | null>(
    null,
  );
  const [isCopied, setIsCopied] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  // Sorting state
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<string | null>(null);

  // Use id (not data-pixel-id) so the attribute survives GTM's Custom HTML
  // sanitizer, which strips data-* attributes. SDK falls back to data-pixel-id
  // for any snippets already deployed from a prior version of this emitter.
  const pixelScriptCode = useMemo(() => {
    if (!pixelCode) return '';

    return `<script src="${PIXEL_CDN_URL}" id="${pixelCode}"></script>`;
  }, [pixelCode]);

  const handleCopyPixelCode = async () => {
    if (pixelScriptCode) {
      try {
        copyToClipboard(pixelScriptCode);
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy pixel code:', err);
      }
    }
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setEditingRuleIndex(undefined);
    setShowAddRuleModal(true);
  };

  const handleEditRule = (rule: ConversionRule, index: number) => {
    setEditingRule(rule);
    setEditingRuleIndex(index);
    setShowAddRuleModal(true);
  };

  const handleDeleteRuleClick = (rule: ConversionRule, index: number) => {
    setDeletingRule({ rule, index });
    setShowDeleteRuleModal(true);
  };

  const handleSaveRule = async (rule: ConversionRule) => {
    if (!advertiserId) return;

    const currentConversions = config?.conversions || [];
    let updatedConversions: ConversionRule[];

    if (editingRule) {
      // Editing existing rule - find by matching properties
      updatedConversions = currentConversions.map((existingRule) => {
        if (
          existingRule.name === editingRule.name &&
          existingRule.category === editingRule.category &&
          existingRule.subcategory === editingRule.subcategory
        ) {
          return rule;
        }

        return existingRule;
      });
    } else {
      // Adding new rule
      updatedConversions = [...currentConversions, rule];
    }

    const updatedConfig: PixelConfigDTO = {
      ...(config || {}),
      conversions: updatedConversions,
      trackPageView: config?.trackPageView ?? false,
      pixelId: pixelCode || '',
      version: config?.version ?? null,
      dspMappings: config?.dspMappings ?? null,
    };

    const success = await saveConfig(updatedConfig);

    if (success) {
      setShowAddRuleModal(false);
      setEditingRule(null);
      setEditingRuleIndex(undefined);
    } else {
      throw new Error('Failed to save config');
    }
  };

  const handleDeleteRule = async () => {
    if (!advertiserId || !config || !deletingRule) return;

    const currentConversions = config.conversions || [];
    const ruleToDelete = deletingRule.rule;
    const updatedConversions = currentConversions.filter(
      (rule) =>
        !(
          rule.name === ruleToDelete.name &&
          rule.category === ruleToDelete.category &&
          rule.subcategory === ruleToDelete.subcategory
        ),
    );

    const updatedConfig: PixelConfigDTO = {
      ...config,
      conversions: updatedConversions,
    };

    const success = await saveConfig(updatedConfig);

    if (success) {
      setShowDeleteRuleModal(false);
      setDeletingRule(null);
    } else {
      throw new Error('Failed to delete rule');
    }
  };

  const handleCloseRuleModal = () => {
    setShowAddRuleModal(false);
    setEditingRule(null);
    setEditingRuleIndex(undefined);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteRuleModal(false);
    setDeletingRule(null);
  };

  const handlePublish = async () => {
    if (!advertiserId) return;

    setIsPublishing(true);
    try {
      // Call API directly - API returns 200 but may have error in response body
      const response = await pixelApi.publishPixelConfig(advertiserId);

      // Check if response contains an error message (API returns 200 with error in body)
      const responseData = response as unknown as {
        message?: string;
        error?: string;
        success?: boolean;
      };

      if (responseData?.error || (responseData?.message && !responseData?.success)) {
        // Show error message from response
        const errorMessage =
          responseData?.error || responseData?.message || 'Failed to publish pixel config';

        toast.error(errorMessage, {
          position: 'bottom-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false,
          theme: 'light',
        });
      } else {
        // Success - refresh config and show success message
        await fetchConfig();
        NotificationService.success('Pixel config published successfully');
      }
    } catch (err: unknown) {
      // Handle actual HTTP errors (non-200 status)
      const axiosError = err as {
        response?: {
          status?: number;
          data?: { message?: string; error?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.response?.data?.error ||
        axiosError?.message ||
        'Failed to publish pixel config';

      toast.error(errorMessage, {
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
        theme: 'light',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // Sort conversions array based on sort state
  const sortedConversions = useMemo(() => {
    if (!config?.conversions || !sortBy || !sortDirection) {
      return config?.conversions || [];
    }

    const sorted = [...(config.conversions || [])];

    sorted.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'subcategory':
          aValue = a.subcategory || '';
          bValue = b.subcategory || '';
          break;
        case 'triggerType':
          aValue = a.trigger?.[0]?.type || '';
          bValue = b.trigger?.[0]?.type || '';
          break;
        case 'triggerFrequency':
          aValue = a.frequency || '';
          bValue = b.frequency || '';
          break;
        default:
          return 0;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (sortDirection === 'ASC') {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    return sorted;
  }, [config?.conversions, sortBy, sortDirection]);

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ConversionRule> | SorterResult<ConversionRule>[],
    ) => {
      const isArray = Array.isArray(sorter);

      setSortBy(isArray ? null : (sorter.field as string));
      setSortDirection(!isArray && sorter.order ? sortDirections[sorter.order] : null);
    },
    [],
  );

  // Table columns definition
  const columns: TableProps<ConversionRule>['columns'] = [
    {
      dataIndex: 'name',
      title: 'Name',
      width: 200,
      sorter: true,
      sortIcon,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'category',
      title: 'Category',
      width: 150,
      sorter: true,
      sortIcon,
      showSorterTooltip: false,
    },
    {
      dataIndex: 'subcategory',
      title: 'Subcategory',
      width: 150,
      sorter: true,
      sortIcon,
      showSorterTooltip: false,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      dataIndex: 'triggerType',
      title: 'Trigger Type',
      width: 150,
      sorter: true,
      sortIcon,
      showSorterTooltip: false,
      render: (_: unknown, record: ConversionRule) =>
        record.trigger?.length ? record.trigger.map((t) => t.type).join(', ') : '-',
    },
    {
      dataIndex: 'triggerFrequency',
      key: 'triggerFrequency',
      title: 'Trigger Frequency',
      width: 150,
      sorter: true,
      sortIcon,
      showSorterTooltip: false,
      render: (_: unknown, record: ConversionRule) =>
        formatFrequency(record.frequency as FrequencyType | null | undefined),
    },
    {
      dataIndex: 'actions',
      title: '',
      width: 100,
      render: (_: unknown, record: ConversionRule, index: number) => (
        <div className="grow shrink basis-0 justify-between items-center flex">
          <IconButton
            variant="tertiary"
            className="bg-transparent hover:bg-primary-gray-25"
            size="sm"
            Icon={Edit}
            onClick={() => handleEditRule(record, index)}
          />
          <IconButton
            variant="tertiary"
            className="bg-transparent hover:bg-primary-gray-25"
            size="sm"
            Icon={Trash2}
            onClick={() => handleDeleteRuleClick(record, index)}
          />
        </div>
      ),
    },
  ];

  if (error && !isLoading) {
    return (
      <div className="w-full bg-white rounded-xl border border-primary-gray-100 flex items-center justify-center h-64">
        <p className="text-system-error-500 text-base-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-6">
      {/* Single White Box containing Universal Pixel and Conversion Rules */}
      <div className="w-full bg-white rounded-xl border border-primary-gray-100 flex-col justify-start items-start flex">
        <div className="p-8 w-full flex-col justify-start items-start gap-8 inline-flex">
          {/* Universal Pixel Section */}
          <div className="flex-col justify-start items-start gap-2 flex">
            <div className="flex-col justify-start items-start gap-2 inline-flex">
              <div className="text-secondary-black text-xl font-semibold leading-relaxed">
                Universal pixel
              </div>
              <div className="self-stretch text-primary-gray-500 text-base-sm font-normal leading-5">
                Welcome! To get started, place your Universal pixel. The pixel can be used to track
                a variety of things, due to this, the pixel code and how it is placed varies. Please
                refer to the pixel implementation guide for documentation on how to set it up.
              </div>
            </div>
          </div>

          {/* Pixel Code Display */}
          <div className="w-full justify-start items-start gap-2 flex">
            <div className="grow shrink basis-0 flex-col justify-start items-start gap-1.5 flex">
              <div className="grow shrink basis-0 text-secondary-black text-base-xs font-medium leading-none">
                Your pixel code
              </div>
              <div className="gap-2 self-stretch inline-flex">
                {pixelCode ? (
                  <>
                    <Input disabled value={pixelScriptCode} className="flex-1 font-mono" />
                    <IconButton
                      Icon={isCopied ? Check : Copy}
                      variant={isCopied ? 'primary' : 'outline'}
                      size="md"
                      onClick={handleCopyPixelCode}
                    />
                  </>
                ) : (
                  <div className="flex items-center gap-2 rounded-lg border border-primary-gray-100 bg-primary-gray-25 px-3 py-2 w-full">
                    <p className="text-base-sm text-primary-gray-500">
                      Pixel code is being loaded...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Conversion Rules Section */}
          <div className="w-full flex-col justify-start items-start gap-4 inline-flex">
            <div className="w-full flex items-center justify-between">
              <div className="text-secondary-black text-xl font-semibold leading-relaxed">
                Conversion Rules
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={handleAddRule} Icon={PlusCircle}>
                  Add Rule
                </Button>
                <Button
                  variant="primary"
                  onClick={handlePublish}
                  disabled={isPublishing || !advertiserId}
                >
                  {isPublishing ? 'Publishing...' : 'Publish'}
                </Button>
              </div>
            </div>

            {/* Conversion Rules Table */}
            <div className="w-full">
              <Spin spinning={isLoading}>
                <Table
                  dataSource={sortedConversions}
                  columns={columns}
                  pagination={false}
                  onChange={handleTableChange}
                  rowKey={(record, index) => `${record.name}-${record.category}-${index}`}
                  locale={{
                    emptyText: (
                      <div className="flex flex-col items-center justify-center py-16">
                        <svg
                          width="64"
                          height="41"
                          viewBox="0 0 64 41"
                          xmlns="http://www.w3.org/2000/svg"
                          className="mb-4"
                        >
                          <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                            <ellipse fill="#F5F5F5" cx="32" cy="33" rx="32" ry="7" />
                            <g fillRule="nonzero" stroke="#D9D9D9">
                              <path d="M55 12.76L44.854 1.258H22.147L11 12.761V37.297H55V12.76z" />
                              <path
                                d="M22.147 1.258v9.76L11 22.297M55 12.76H44.854M44.854 1.258v9.76M11 12.761v10.24M11 22.297h10.997M22.147 22.297h10.997"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </g>
                          </g>
                        </svg>
                        <span className="text-base-sm text-primary-gray-300">
                          No conversion rules configured
                        </span>
                      </div>
                    ),
                  }}
                />
              </Spin>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Conversion Rule Modal */}
      <ConversionRuleModal
        isOpen={showAddRuleModal}
        onClose={handleCloseRuleModal}
        onSave={handleSaveRule}
        editingRule={editingRule}
        editingIndex={editingRuleIndex}
      />

      {/* Delete Confirmation Modal */}
      <DeleteRuleModal
        isOpen={showDeleteRuleModal}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteRule}
        ruleName={deletingRule?.rule.name || ''}
      />
    </div>
  );
};
